import { useState, useEffect } from 'react';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { IconHelpChatOutline, IconChevronArrow } from '../Icon/Index';

type SidebarItemType = {
  label: string;
  icon?: React.ReactNode;
  iconActive?: React.ReactNode;
  activeUrl?: string[];
  isIndex?: boolean;
  onClick?: () => void;
  children?: SidebarItemType[];
};

type SidebarType = {
  title: string;
  items: SidebarItemType[];
  logoImage: string;
  /**
   * Pass react router dom v6 location object here
   * @TODO make this generic type
   */
  location: any;
  onClickLogo?: () => void;
  openSidebar?: boolean;
  hasSupportChat?: boolean;
  supportChatLoading?: boolean;
  onSupportChatClick?: () => void;
  versionText?: string;
};

const tvsLogo: string =
  'https://stblobmftprodsharedv2.blob.core.windows.net/mft-app-image-assets/tvsd/ui/powered_by_tvsd.svg';
const apecLogo: string =
  'https://stblobmftprodsharedv2.blob.core.windows.net/mft-app-image-assets/tvsd/ui/apec_prp_logo.svg';

const Sidebar = (props: SidebarType) => {
  const {
    title,
    items,
    location,
    logoImage,
    openSidebar = true,
    onClickLogo = null,
    hasSupportChat = true,
    supportChatLoading = false,
    onSupportChatClick,
    versionText = 'Version 6.0',
  } = props;
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [currentUrl, setCurrentUrl] = useState<string>('');
  const [openMobileSidebar, setOpenMobileSidebar] =
    useState<boolean>(openSidebar);

  const renderChildren = (children: SidebarItemType[], parentIndex: number) => {
    return (
      <ul
        className={`theme-sidebar-children ${
          activeIndex !== parentIndex && 'theme-sidebar-children-hidden'
        }`}
      >
        {children.map((child: SidebarItemType, index: number) => {
          const childUrls: string[] | undefined = child.activeUrl;
          const checkIsActive: boolean =
            childUrls !== undefined
              ? childUrls.some((url: string) => {
                  return currentUrl.includes(url);
                })
              : false;
          return (
            <li
              key={`sidebar-${parentIndex}-${index}`}
              className={`${checkIsActive && 'theme-sidebar-item-active'}`}
            >
              <div
                className='theme-sidebar-child-item'
                onClick={() => child.onClick && child.onClick()}
              >
                <span>{child.label}</span>
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  useEffect(() => {
    setCurrentUrl(window.location.href);
    setOpenMobileSidebar(false);
  }, [location]);

  useEffect(() => {
    setOpenMobileSidebar(openSidebar);
  }, [openSidebar]);

  return (
    <aside
      className={`theme-sidebar ${openMobileSidebar && 'theme-sidebar-open'}`}
    >
      <div className='theme-sidebar-wrapper'>
        <a
          href='#'
          onClick={() => {
            setActiveIndex(-1);
            onClickLogo && onClickLogo();
          }}
          className='theme-sidebar-logo-wrapper'
        >
          <img
            src={logoImage}
            alt='TVS Digital'
            className='theme-sidebar-logo'
          />
        </a>
        <OverlayScrollbarsComponent defer>
          <ul className='theme-sidebar-menu'>
            <li className='theme-sidebar-title'>{title}</li>
            {items.map((item: SidebarItemType, index: number) => {
              const checkIsActive =
                item.activeUrl?.some(url => currentUrl.includes(url)) ||
                (item.isIndex !== undefined &&
                  item.isIndex &&
                  window.location.href.replace('#', '') ===
                    `${window.location.origin}/`);

              return (
                <li key={`sidebar-${index}`}>
                  <button
                    className={`theme-sidebar-link" ${
                      checkIsActive ? 'theme-sidebar-item-active' : ''
                    }`}
                    onClick={() => {
                      setActiveIndex(activeIndex === index ? -1 : index);
                      item.onClick && item.onClick();
                    }}
                  >
                    {item.icon && item.iconActive && (
                      <div className='theme-sidebar-icon'>
                        {checkIsActive ? item.iconActive : item.icon}
                      </div>
                    )}
                    {item.label}
                    {item.children !== undefined &&
                      Array.isArray(item.children) &&
                      item.children.length > 0 && (
                        <span className='theme-sidebar-arrow'>
                          <IconChevronArrow
                            fill='#395278'
                            className={`theme-sidebar-arrow ${
                              index === activeIndex && 'is-opened'
                            }`}
                          />
                        </span>
                      )}
                  </button>
                  {item.children !== undefined &&
                    Array.isArray(item.children) &&
                    item.children.length > 0 &&
                    renderChildren(item.children, index)}
                </li>
              );
            })}
          </ul>
        </OverlayScrollbarsComponent>

        <div className='theme-sidebar-footer'>
          <p className='theme-sidebar-version-text'>{versionText}</p>
          <div className='logo-wrapper'>
            <img src={tvsLogo} alt='Powered By TVS' className='tvs-logo' />
            <img src={apecLogo} alt='APEC Logo' className='apec-logo' />
          </div>
          {hasSupportChat && (
            <div
              className='support-chat'
              onClick={() =>
                onSupportChatClick &&
                !supportChatLoading &&
                onSupportChatClick()
              }
            >
              <IconHelpChatOutline fill='#3548C9' />
              <span>
                {supportChatLoading ? 'Loading Chat...' : 'Chat Support'}
              </span>
            </div>
          )}
          <div className='content-wrapper'>
            <a
              href='https://www.tvsd.ai/en/privacy-policy'
              className='color-theme-grey-200'
              target='_blank'
            >
              Privacy Policy
            </a>
            <a
              href='https://www.tvsd.ai/en/terms-of-use'
              className='color-theme-grey-200'
              target='_blank'
            >
              Terms Of Use
            </a>
            <a
              href='https://www.tvsd.ai/en/cookie-policy'
              className='color-theme-grey-200'
              target='_blank'
            >
              Cookie Policy
            </a>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
